import { useDispatch, useSelector } from 'react-redux'
import { selectSlotList, setSlotList } from '../../../store/slices/slotListSlice'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { setCommonModalState } from '../../../store/slices/commonModalSlice'
import { loaderDelay, SLOT_FORM_ROUTE, slotFilterPattern } from '../../../common/utils/constants'
import { useCallback, useEffect, useState } from 'react'
import { CommonModals, Group, ListPageType } from '../../../common/types/enums'
import {
  CityInterface,
  ListParams,
  OptionItemInterface,
  Range,
  SlotItemInterface,
  SlotListResponse,
  SortOption,
  SortParams,
  TypesObject,
} from '../../../common/types/interfaces'
import { getApi } from '../../../api/api'
import { addYears, endOfDay, startOfDay, subYears } from 'date-fns'
import { selectCurrentUser } from '../../../store/slices/currentUserSlice'
import { selectMapSliderVisible, toggleMapSlider } from '../../../store/slices/gMapSlice'
import { delayAction } from '../../../common/utils/functions/delayAction'

const rangeInitialValue = {
  from: startOfDay(new Date(subYears(new Date(), 1))),
  to: endOfDay(new Date(addYears(new Date(), 1))),
}

const sortOptions: SortOption[] = [
  {
    id: 1,
    name: 'По наименованию',
    value: 'name',
  },
  {
    id: 2,
    name: 'По описанию',
    value: 'description',
  },
  {
    id: 3,
    name: 'По времени подачи',
    value: 'startTime',
  },
  {
    id: 4,
    name: 'По ставке',
    value: 'cost',
  },
]

const typesPattern = {
  transport_types: [],
  equipment_types: [],
  cargo_types: [],
  transport_side_types: [],
  transport_load_capacities: [],
}

const sortInitialValue: SortParams = { sort: 'startTime', order: 'ASC' }

export const useSlot = () => {
  const list = useSelector(selectSlotList)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const setLoginModalState = (state: boolean) =>
    dispatch(setCommonModalState({ modal: CommonModals.LOGIN, state }))
  const [cityList, setCityList] = useState<CityInterface[]>([])
  const [api] = useState(getApi())
  const [filtersDirty, setFiltersDirty] = useState(false)
  const { hash } = useLocation()
  const mapVisible = useSelector(selectMapSliderVisible)
  const { pageType } = useParams()
  const [loading, setLoading] = useState<boolean>(false)
  const [filtersChanged, setFiltersChanged] = useState<boolean>(false)

  const filtersString = localStorage.getItem(`slotFilters${pageType ? pageType : ''}`)
  const filters: ListParams = filtersString ? JSON.parse(filtersString) : {}

  const {
    lte: toParam,
    gte: fromParam,
    sort: sortParam,
    order: orderParam,
    search: searchParam,
    cityId: cityIdParam,
    status: statusParam,
    userId: userIdParam,
    types: typesParam,
  } = filters ? filters : {}

  const [types, setTypes] = useState<TypesObject>(typesParam ? typesParam : typesPattern)

  //Search and filters
  //Date range
  const [range, setRange] = useState<Range>({
    from: fromParam ? new Date(fromParam) : rangeInitialValue.from,
    to: toParam ? new Date(toParam) : rangeInitialValue.to,
  })
  const [datesValid, setDatesValid] = useState<boolean>(true)
  //Sort
  const [sort, setSort] = useState<SortParams>({
    sort: sortParam ? sortParam : sortInitialValue.sort,
    //@ts-ignore
    order: orderParam ? orderParam : sortInitialValue.order,
  })
  //City
  const [cityId, setCityId] = useState<string>(cityIdParam ? cityIdParam : '')
  //Status
  //@ts-ignore
  const [status, setStatus] = useState<string>(statusParam ? statusParam : '')
  //My
  const [userId, setUserId] = useState<string>(userIdParam ? userIdParam : '')
  //Search
  const [searchFields] = useState<string[]>(['name', 'description']) //not date, not id, object field or path to array with key
  const [search, setSearch] = useState<string>(searchParam ? searchParam : '')
  //Methods
  const onChangeSearch = (value: string) => {
    setSearch(value)
  }

  const onChangeRange = (value: string, first?: boolean) => {
    setRange((prev) => {
      if (first) {
        return { ...prev, from: new Date(value) }
      }
      return { ...prev, to: new Date(value) }
    })
  }
  const clearRange = (value: Range) => {
    setRange(value)
  }
  const onChangeSort = (value: SortParams) => {
    setSort(value)
  }
  const setCity = (value: string) => {
    setCityId(value)
  }

  const clearSearch = () => {
    setSearch('')
  }

  const getCityList = useCallback(async () => {
    try {
      const result = await api.getCityList()
      setCityList(result)
    } catch (error: any) {
  throw error
}
  }, [api])

  const createCitySelectList = () => {
    let arr: OptionItemInterface[] = [
      { id: '', label: `Все города`, name: '', value: '' },
    ]
    for (const item of cityList) {
      let pattern: OptionItemInterface = {
        id: String(item.id),
        label: `${item.value}`,
        name: item.value,
        value: String(item.id),
      }
      arr.push(pattern)
    }
    return arr
  }

  const addAction = () => {
    if (currentUser) {
      navigate(SLOT_FORM_ROUTE)
      // navigate(0)
      return
    }
    localStorage.setItem('afterActionRoute', SLOT_FORM_ROUTE)
    setLoginModalState(true)
  }

  const getSlotList = useCallback(
    async (withLoader?: boolean) => {
      try {
        const filters = localStorage.getItem(`slotFilters${pageType ? pageType : ''}`)
        const { types: storageTypes, ...rest } = JSON.parse(filters ? filters : JSON.stringify({}))
        let result: SlotListResponse
        if (withLoader) {
          setLoading(true)
          await delayAction(loaderDelay)
        }
        if (currentUser) {
          result = await api.getSlotList(
            filters
              ? {
                  ...rest,
                  ...storageTypes,
                  selected: pageType === ListPageType.SELECTED ? '1' : '',
                  userId: pageType === ListPageType.MY ? currentUser.id : '',
                  cityId: cityId ? cityId : '',
                  status: status ? status : '',
                }
              : {
                  ...slotFilterPattern,
                  selected: pageType === ListPageType.SELECTED ? '1' : '',
                  userId: pageType === ListPageType.MY ? currentUser.id : '',
                  searchFields,
                  gte: rangeInitialValue.from,
                  lte: rangeInitialValue.to,
                  ...typesPattern,
                  cityId: '',
                  status: '',
                },
          )
        } else {
          result = await api.getAdSlotList(
            filters
              ? {
                  ...rest,
                  ...storageTypes,
                  cityId: cityId ? cityId : '',
                  status: status ? status : '',
                }
              : {
                  ...slotFilterPattern,
                  searchFields,
                  gte: rangeInitialValue.from,
                  lte: rangeInitialValue.to,
                  ...typesPattern,
                  cityId: '',
                  status: '',
                },
          )
        }
        dispatch(setSlotList(result))
        if (withLoader) setLoading(false)
      } catch (error) {
        if (withLoader) setLoading(false)
      }
    },
    [api, dispatch, currentUser, pageType, searchFields, cityId, status],
  )

  const activateFilters = useCallback(async () => {
    try {
      localStorage.setItem(
        `slotFilters${pageType ? pageType : ''}`,
        JSON.stringify({
          lte: new Date(range.to),
          gte: new Date(range.from),
          cityId,
          ...sort,
          search,
          searchFields,
          userId,
          status,
          types,
        }),
      )
      await getSlotList()
      setFiltersChanged(false)
    } catch (error) {}
  }, [
    cityId,
    getSlotList,
    range.from,
    range.to,
    search,
    searchFields,
    sort,
    status,
    userId,
    pageType,
    types,
  ])

  const clearFilters = useCallback(async () => {
    setCity('')
    setStatus('')
    setUserId('')
    setDatesValid(true)
    setTypes(typesPattern)
    clearRange(rangeInitialValue)
    localStorage.removeItem(`slotFilters${pageType ? pageType : ''}`)
    await await getSlotList()
  }, [getSlotList, pageType])

  const toggleSelected = useCallback(
    async (item: SlotItemInterface) => {
      try {
        const id = item.selected?.find((el: any) => el.user_id === currentUser?.id)?.id
        if (item && id) {
          await api.deleteSelected({ id }, Group.SLOT)
        } else if (item.id) {
          await api.addSelected({ el_id: item.id }, Group.SLOT)
        }
        await getSlotList()
      } catch (error) {}
    },
    [api, currentUser?.id, getSlotList],
  )

 

  useEffect(() => {
    window.scrollTo(0, 0)
    getSlotList(true)
    getCityList()
  }, [])

  useEffect(() => {
    setFiltersDirty(
      !Boolean(cityId !== '') &&
        !Boolean(status !== '') &&
        !Boolean(userId) &&
        JSON.stringify(range) === JSON.stringify(rangeInitialValue) &&
        JSON.stringify(typesPattern) === JSON.stringify(types),
    )
    setFiltersChanged(true)
  }, [range, cityId, status, userId, types])

  useEffect(() => {
    activateFilters()
  }, [search])

  useEffect(() => {
    if (hash) {
      const targetElement = document.getElementById(hash.substring(1))
      if (!mapVisible) {
        setTimeout(() => {
          dispatch(toggleMapSlider())
        }, 500)
      }
      targetElement?.scrollIntoView({ behavior: 'smooth' })
      if (window.location.hash) {
        window.history.replaceState('', document.title, window.location.pathname)
      }
    }
  }, [hash])

  return {
    list,
    addAction,
    createCitySelectList,
    onChangeSearch,
    onChangeRange,
    onChangeSort,
    clearSearch,
    datesValid,
    sortOptions,
    setCity,
    sort,
    search,
    filtersDirty,
    range,
    cityId,
    clearFilters,
    activateFilters,
    setStatus,
    setUserId,
    userId,
    status,
    toggleSelected,
    pageType,
    loading,
    types,
    setTypes,
    filtersChanged,
  }
}
